/* eslint-disable max-lines */
/* eslint-disable react/prop-types */
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Pagination,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import visuallyHidden from '@mui/utils/visuallyHidden';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { find, isEmpty, isNil, map, toLower } from 'lodash';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import ReshippingIcon from 'common/components/ReshippingIcon';
import orderFillType from 'common/constants/orderFillType';
import { PrescriptionFormattedStatus } from 'common/constants/prescriptionStatus';
import FacilityAvatar from 'common/modules/FacilityAvatar';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { formatEnumLabel, formatShipByDate, sortTags } from 'common/utils';
import formatDrugLabel from 'common/utils/formatDrugLabel';
import ShipByDateCell from 'common/components/ShipByDateCell';
import { SortKey, SortOrder } from 'enums/common.enum';
import { orderSource } from 'enums/order.enum';
import WorkQueues from 'enums/workQueue.enum';
import { workQueueActions } from 'store/slices/workQueueSlice';
import { updateBatchShipmentFilter } from 'store/thunks/batchShipmentThunk';
import { upsertLineItemTags } from 'store/thunks/tagThunks';
import {
  getPrescriptionsNeedsReviewByPage,
  sortPrescriptionsNeedsReview,
} from 'store/thunks/workQueueThunks';
import PrescriptionReviewOptions from './PrescriptionReviewOptions';
import {
  PrescriptionSelectionCheckBox,
  SelectAllPrescriptionsCheckBox,
} from './PrescriptionReviewSelectionContext';

const TagsPopoverContent = ({
  selectedTagsLineItem,
  loadingLineItems,
  formatTagText,
  handleRemoveTag,
  handleRemoveAllTags,
}) => {
  const isLoading = loadingLineItems.has(selectedTagsLineItem.lineItemId);
  const tags = (selectedTagsLineItem.tags || []).filter(
    (tag) => tag?.tagName !== 'BACKFILLED_SHIP_BY'
  );

  return (
    <Box sx={{ p: 1.5, width: '400px', position: 'relative' }}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          minHeight: '56px',
          maxHeight: '200px',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '4px',
          bgcolor: '#fff',
          '&:hover': {
            borderColor: '#1976d2',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            flex: 1,
            maxHeight: '180px',
            overflowY: 'auto',
            p: 1,
            pr: '40px',
            mr: '-23px',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
        >
          {tags.map((tag, index) => {
            const tagKey = tag?.tagName || '';
            const isSystemTag = tag?.isSystemTag;
            return (
              <Chip
                key={tagKey || index}
                label={formatTagText(tag)}
                size='small'
                onDelete={
                  !isSystemTag ? () => handleRemoveTag(selectedTagsLineItem, tag) : undefined
                }
                deleteIcon={!isSystemTag ? <CloseIcon /> : undefined}
                disabled={isLoading}
                sx={{
                  '& .MuiChip-label': {
                    whiteSpace: 'nowrap',
                  },
                  ...(isSystemTag && { opacity: 0.7, cursor: 'default' }),
                }}
              />
            );
          })}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            zIndex: 1,
          }}
        >
          <Tooltip title='Remove all tags' arrow placement='top'>
            <IconButton
              size='small'
              onClick={() => handleRemoveAllTags(selectedTagsLineItem)}
              disabled={isLoading}
              sx={{
                bgcolor: '#FFD54F',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: '#FFC107',
                },
                '&.Mui-disabled': {
                  bgcolor: '#F5F5F5',
                  opacity: 0.6,
                },
              }}
            >
              <CloseIcon
                sx={{
                  fontSize: '18px',
                  color: '#000',
                  fontWeight: 'bold',
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <CircularProgress size={27} />
        </Box>
      )}
    </Box>
  );
};

const TagsTableCell = ({ lineItem, isLoading, formatTagText, handleTagsClick }) => {
  const allTags = (lineItem.tags || []).filter((tag) => tag?.tagName !== 'BACKFILLED_SHIP_BY');
  const systemTags = allTags.filter((tag) => tag.isSystemTag);
  const userTags = allTags.filter((tag) => !tag.isSystemTag);

  if (allTags.length === 0) {
    return null;
  }

  const handleClick = (event) => {
    if (!isLoading) {
      handleTagsClick(event, lineItem);
    }
  };

  const firstUserTag = userTags[0];
  const remainingUserTags = userTags.slice(1);

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.5 }}>
      {/* System tags — always fully visible, no interaction */}
      {systemTags.length > 0 && (
        <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
          {systemTags.map((tag) => (
            <Chip
              key={tag.tagName}
              label={formatTagText(tag)}
              size='small'
              color={tag.tagName === 'URGENT' ? 'error' : 'default'}
              sx={{
                cursor: 'default',
                ...(tag.tagName !== 'URGENT' && { opacity: 0.7 }),
                ...(tag.tagName === 'URGENT' && { fontWeight: 600 }),
              }}
            />
          ))}
        </Box>
      )}

      {/* User tags — first tag clickable to edit, +N shows tooltip with remaining */}
      {userTags.length > 0 && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: isLoading ? 'default' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          <Tooltip title='Click to manage tags' arrow placement='top'>
            <Chip
              label={formatTagText(firstUserTag)}
              size='small'
              disabled={isLoading}
              onClick={handleClick}
              sx={{
                maxWidth: '120px',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Tooltip>

          {remainingUserTags.length > 0 && (
            <Tooltip
              title={remainingUserTags.map((tag) => formatTagText(tag)).join(', ')}
              arrow
              placement='top'
            >
              <Chip
                label={`+${remainingUserTags.length}`}
                size='small'
                variant='outlined'
                disabled={isLoading}
                sx={{ minWidth: '40px', cursor: 'default' }}
              />
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

const headCells = [
  {
    id: SortKey.ORDER_ID,
    label: 'Order Id',
    sortable: true,
  },
  {
    id: SortKey.STATUS,
    label: 'Status',
    sortable: false,
  },
  {
    id: SortKey.DRUG_NAME,
    label: 'Drug Name',
    sortable: true,
  },
  // Not sortable: listPrescriptionForReview has no ndc sort key.
  {
    id: 'ndc',
    label: 'NDC',
    sortable: false,
  },
  {
    id: SortKey.PATIENT_NAME,
    label: 'Patient',
    sortable: true,
  },
  {
    id: SortKey.RX_NUMBER,
    label: 'Rx Number',
    sortable: true,
  },
  {
    id: 'px',
    label: 'Px',
    sortable: false,
  },
  {
    id: SortKey.FILL_TYPE,
    label: 'Fill Type',
    sortable: true,
  },
  {
    id: 'shippingOption',
    label: 'Shipping Option',
    sortable: false,
  },
  {
    id: SortKey.REQUESTED_DATE,
    label: 'Requested Date',
    sortable: true,
  },
  {
    id: SortKey.SHIP_BY_DATE,
    label: 'Ship By Date',
    sortable: true,
  },
  {
    id: SortKey.NEEDS_BY_DATE,
    label: 'Needs By Date',
    sortable: true,
  },
  {
    id: 'source',
    label: 'Source',
    sortable: false,
  },
  {
    id: 'tags',
    label: 'Tags',
    sortable: false,
  },
];

const PrescriptionReviewTable = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tagsAnchorEl, setTagsAnchorEl] = useState(null);
  const [selectedTagsLineItem, setSelectedTagsLineItem] = useState(null);
  const [loadingLineItems, setLoadingLineItems] = useState(new Set());

  const { sortBy, pages, currentPage } = useSelector(
    ({ workQueue }) => workQueue.prescriptionReview
  );
  const currentTenantId = useSelector(({ auth }) => auth?.currentTenantId);
  const facilities = useSelector(({ pharmacy }) => pharmacy[currentTenantId]);

  const shippingOptions = useSelector(({ systemConfigs }) => systemConfigs.shippingOptions || []);

  const totalPages = useSelector(({ workQueue }) =>
    Math.ceil(workQueue.prescriptionReview.count / 25)
  );
  const prescriptions = pages[currentPage];

  const allLineItemIds = (prescriptions || [])
    .filter((prescription) => prescription.lineItem?.requestLineItemId)
    .map((prescription) => prescription.lineItem.requestLineItemId);

  const formatTagText = (tag) => {
    const tagName = tag?.tagName || '';
    return tagName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      sortPrescriptionsNeedsReview({
        sortBy: {
          sortKey,
          sortOrder:
            sortBy?.sortKey === sortKey && sortBy?.sortOrder === SortOrder.ASCENDING
              ? SortOrder.DESCENDING
              : SortOrder.ASCENDING,
        },
      })
    );
  };
  const handleCopyLineItemId = (event, lineItemId) => {
    event.stopPropagation();
    navigator.clipboard.writeText(lineItemId);
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) {
      // page didnt change, don't do anything.
      return;
    }

    dispatch(getPrescriptionsNeedsReviewByPage({ page }));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToBatchShipmentQueue = (orderId) => {
    dispatch(
      updateBatchShipmentFilter({
        filter: { orderId },
      })
    );
    navigate('/batchShipments');
  };

  const handleTagsClick = (event, lineItem) => {
    event.stopPropagation();
    setTagsAnchorEl(event.currentTarget);
    setSelectedTagsLineItem(lineItem);
  };

  const handleTagsClose = () => {
    setTagsAnchorEl(null);
    setSelectedTagsLineItem(null);
  };

  const handleRemoveTag = (lineItem, tagToRemove) => {
    const lineItemId = lineItem?.lineItemId;
    if (!lineItemId || loadingLineItems.has(lineItemId)) {
      return;
    }
    const currentTags = lineItem.tags || [];
    const tagToRemoveName = tagToRemove?.tagName || '';
    const updatedTags = currentTags.filter((tag) => tag.tagName !== tagToRemoveName);
    setLoadingLineItems((prev) => new Set(prev).add(lineItemId));

    const nonSystemTags = updatedTags.filter((tag) => !tag.isSystemTag);
    if (nonSystemTags.length === 0) {
      handleTagsClose();
    }

    dispatch(
      upsertLineItemTags({
        lineItemId,
        currentTags,
        newTags: updatedTags,
      })
    )
      .then(() => {
        dispatch(
          workQueueActions.updateLineItemTags({
            preset: WorkQueues.PRESCRIPTION_REVIEW,
            page: currentPage,
            lineItemId,
            tags: updatedTags,
          })
        );
        if (nonSystemTags.length > 0) {
          setSelectedTagsLineItem((prev) => ({
            ...prev,
            tags: updatedTags,
          }));
        }
      })
      .finally(() => {
        setLoadingLineItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(lineItemId);
          return newSet;
        });
      });
  };

  const handleRemoveAllTags = (lineItem) => {
    setLoadingLineItems((prev) => new Set(prev).add(lineItem.lineItemId));
    handleTagsClose();

    const systemTagsToKeep = (lineItem.tags || []).filter((tag) => tag.isSystemTag);

    return dispatch(
      upsertLineItemTags({
        lineItemId: lineItem.lineItemId,
        currentTags: lineItem.tags || [],
        newTags: systemTagsToKeep,
      })
    )
      .then(() => {
        dispatch(
          workQueueActions.updateLineItemTags({
            preset: WorkQueues.PRESCRIPTION_REVIEW,
            page: currentPage,
            lineItemId: lineItem.lineItemId,
            tags: systemTagsToKeep,
          })
        );
      })
      .finally(() => {
        setLoadingLineItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(lineItem.lineItemId);
          return newSet;
        });
      });
  };

  return (
    <Grid container direction='column'>
      <Popover
        open={Boolean(tagsAnchorEl)}
        anchorEl={tagsAnchorEl}
        onClose={handleTagsClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: '400px',
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
              mt: -1,
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: -5,
                left: 5,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '10px 10px 0 10px',
                borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
              },
            },
          },
        }}
      >
        {selectedTagsLineItem?.tags?.length > 0 && (
          <TagsPopoverContent
            selectedTagsLineItem={selectedTagsLineItem}
            loadingLineItems={loadingLineItems}
            formatTagText={formatTagText}
            handleRemoveTag={handleRemoveTag}
            handleRemoveAllTags={handleRemoveAllTags}
          />
        )}
      </Popover>

      <Grid item>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={
                      sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : false
                    }
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={sortBy.sortKey === headCell.id}
                        direction={
                          sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'
                        }
                        onClick={handleSort}
                        data-head-property={headCell.id}
                      >
                        {headCell.label}
                        {sortBy.sortKey === headCell.id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {toLower(sortBy.sortOrder) === SortOrder.DESCENDING
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
                <TableCell align='right' sx={{ py: 0 }}>
                  <SelectAllPrescriptionsCheckBox
                    allLineItemIds={allLineItemIds}
                    isDisabled={isEmpty(allLineItemIds)}
                  />
                </TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {map(prescriptions, (prescription) => {
                const fillingFacility = find(
                  facilities,
                  (facility) => facility.fulfillingPharmacyId === prescription.fulfillingPharmacyId
                );

                const facilityName = fillingFacility?.name;
                const rxPrefix = fillingFacility?.config?.rxPrefix;
                const facilityState = fillingFacility?.state;
                const requestLineItemId = prescription?.lineItem?.requestLineItemId;
                const tags = prescription?.lineItem?.tags;

                return (
                  <TableRow key={prescription.pomPrescriptionId} hover>
                    <TableCell>
                      {prescription.orderId ? (
                        <Link
                          to={`/orders/${prescription.orderId}`}
                          style={{ color: theme.palette.text.primary }}
                        >
                          {prescription.orderId}
                        </Link>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {PrescriptionFormattedStatus[prescription.status] ||
                        formatEnumLabel(prescription.status)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        <Box component='span'>{formatDrugLabel(prescription)}</Box>
                        {requestLineItemId && (
                          <Tooltip
                            title={`ItemId: ${requestLineItemId}`}
                            arrow
                            placement='top'
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: lighten(theme.palette.text.primary, 0.9),
                                  color: theme.palette.text.primary,
                                },
                              },
                              arrow: {
                                sx: {
                                  color: lighten(theme.palette.text.primary, 0.9),
                                },
                              },
                            }}
                          >
                            <IconButton
                              size='small'
                              onClick={(e) => handleCopyLineItemId(e, requestLineItemId)}
                              sx={{ ml: 0.5 }}
                            >
                              <ContentCopyIcon fontSize='inherit' />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {/* An NDC is only bound once the item resolves to a product, so an empty
                        cell is the norm on this queue rather than an error state. */}
                    <TableCell>{prescription.ndc || '-'}</TableCell>
                    <TableCell component='th' scope='row'>
                      <Link
                        to={`/patients/${prescription.patient.mpi}`}
                        style={{ color: theme.palette.text.primary }}
                      >
                        {prescription.patient.patientName}
                      </Link>
                    </TableCell>
                    <TableCell>{prescription.rxNumber}</TableCell>
                    <TableCell>
                      {rxPrefix ? (
                        <FacilityAvatar
                          rxPrefix={rxPrefix}
                          facilityName={facilityName}
                          facilityState={facilityState}
                        />
                      ) : (
                        <Grid />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        sx={{
                          background:
                            prescription.fillType === 'REFILL'
                              ? theme.palette.info.dark
                              : theme.palette.success.dark,
                          color: theme.palette.primary.contrastText,
                          height: 35,
                          width: '70px',
                        }}
                        label={
                          <Typography sx={{ fontSize: '1.1em' }}>
                            {orderFillType[prescription.fillType]}
                          </Typography>
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {prescription.shippingOption
                        ? find(
                            shippingOptions,
                            (option) => option.optionCode === prescription.shippingOption
                          )?.shippingOption || prescription.shippingOption
                        : 'NA'}
                    </TableCell>
                    <TableCell>
                      {format(
                        utcToZonedTime(new Date(prescription.requestedDate), 'utc'),
                        'MM/dd/yyyy'
                      )}
                    </TableCell>
                    <TableCell>
                      <ShipByDateCell
                        shipByDate={prescription.shipByDate}
                        tags={prescription.tags}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'text.disabled' }}>
                      <Tooltip title='Legacy field — see Ship By Date' arrow>
                        <span>{formatShipByDate(prescription.needsByDate)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={orderSource[prescription.source]?.sourceValue || ''}
                        arrow
                        placement='top'
                      >
                        {orderSource[prescription.source]?.sourceKey || 'NA'}
                      </Tooltip>
                    </TableCell>
                    <TableCell align='left'>
                      <TagsTableCell
                        lineItem={{
                          lineItemId: requestLineItemId,
                          tags: tags ? sortTags(tags) : [],
                        }}
                        isLoading={loadingLineItems.has(requestLineItemId)}
                        formatTagText={formatTagText}
                        handleTagsClick={handleTagsClick}
                      />
                    </TableCell>
                    <TableCell align='right' sx={{ py: 0 }}>
                      {requestLineItemId && (
                        <PrescriptionSelectionCheckBox
                          lineItemId={requestLineItemId}
                          isDisabled={false}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          prescription.inBatchShipping
                            ? batchShippingTooltip({
                                isBusinessAddress: prescription.isBusinessAddress,
                                isClickable: true,
                              })
                            : ''
                        }
                        arrow
                        placement='top'
                      >
                        {prescription.inBatchShipping ? (
                          <BatchShippingIcon
                            onClick={() => navigateToBatchShipmentQueue(prescription.orderId)}
                            color={perfectRxTheme.palette.warning.warningColour}
                            showBadge={prescription.isBusinessAddress}
                          />
                        ) : (
                          <Grid>
                            {prescription.isBusinessAddress && <BusinessAddressIndicator />}
                          </Grid>
                        )}
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <PrescriptionReviewOptions
                        status={prescription.status}
                        prescriptionId={prescription.pomPrescriptionId}
                        patientId={prescription.patient.mpi}
                        firstName={prescription.patient?.firstName}
                        lastName={prescription.patient?.lastName}
                        dob={prescription.patient?.dob}
                        email={prescription.patient?.email}
                        gender={prescription.patient?.gender}
                        orderId={prescription.orderId}
                        lineItem={prescription.lineItem?.requestLineItemId}
                        drugName={prescription.prescriptionData?.drug}
                        form={prescription.prescriptionData?.form}
                        strength={prescription.prescriptionData?.strength}
                        fillType={prescription?.fillType}
                        tags={tags || []}
                        patientName={prescription.patient.patientName}
                        isManualReview={prescription.isManualReview}
                      />
                    </TableCell>
                    <TableCell>
                      {prescription.isReshipment ? (
                        <Tooltip title='Item being processed as a reshipment' arrow placement='top'>
                          <span>
                            <ReshippingIcon />
                          </span>
                        </Tooltip>
                      ) : (
                        <Grid />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>

            {!isNil(prescriptions) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={19} align='right' sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(prescriptions) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(prescriptions) && isEmpty(prescriptions) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

export default PrescriptionReviewTable;
